import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { useAuthStore } from "../stores/auth";
import { AuthShell, Field } from "./LoginPage";

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  business: "Business",
};

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50);
}

export function SignupPage() {
  const { t, i18n } = useTranslation();
  const signup = useAuthStore((s) => s.signup);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  const planLabel = selectedPlan ? PLAN_LABELS[selectedPlan] : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signup({
        email,
        password,
        full_name: fullName,
        tenant_name: tenantName,
        tenant_slug: tenantSlug,
        tenant_country: "FR",
        tenant_vat_mode: "standard_fr",
        tenant_currency: "EUR",
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError(t("signupPage.errorDefault"));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell title={t("signupPage.title")} subtitle="HInvoice">
      {planLabel && (
        <div
          role="status"
          className="mb-4 text-sm text-brand-700 bg-brand-500/10 border border-brand-500/20 rounded-md px-3 py-2"
          data-testid="signup-plan-hint"
        >
          {(() => {
            const full = t("signupPage.trialHint", { plan: planLabel });
            const parts = full.split(planLabel!);
            return parts.length === 2
              ? <>{parts[0]}<strong>{planLabel}</strong>{parts[1]}</>
              : <>{full}</>;
          })()}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" aria-label="signup-form">
        <Field label={t("signupPage.fullName")} htmlFor="full_name">
          <input
            id="full_name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="input"
          />
        </Field>
        <Field label={t("signupPage.email")} htmlFor="email">
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
          />
        </Field>
        <Field
          label={t("signupPage.password")}
          htmlFor="password"
          hint={t("signupPage.passwordHint")}
        >
          <input
            id="password"
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
          />
        </Field>

        <hr className="my-2 border-slate-200" />

        <Field label={t("signupPage.companyLong")} htmlFor="tenant_name">
          <input
            id="tenant_name"
            required
            value={tenantName}
            onChange={(e) => {
              setTenantName(e.target.value);
              if (!slugTouched) setTenantSlug(slugify(e.target.value));
            }}
            className="input"
          />
        </Field>
        <Field
          label={t("signupPage.slug")}
          htmlFor="tenant_slug"
          hint={t("signupPage.slugHint")}
        >
          <input
            id="tenant_slug"
            required
            pattern="[a-z0-9-]+"
            value={tenantSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setTenantSlug(e.target.value);
            }}
            className="input"
          />
        </Field>

        <div className="flex items-center justify-between py-1">
          <span className="text-sm text-slate-600">{t("signupPage.language")}</span>
          <div className="flex gap-2">
            {(["fr", "en"] as const).map(lang => (
              <button key={lang} type="button"
                onClick={() => i18n.changeLanguage(lang)}
                className={[
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
                  i18n.language === lang
                    ? "bg-brand-600 text-white border-brand-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
                ].join(" ")}
              >
                <span>{lang === "fr" ? "🇫🇷" : "🇬🇧"}</span>
                <span>{lang === "fr" ? "Français" : "English"}</span>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending} className="btn-primary w-full">
          {pending ? t("signupPage.loading") : t("signupPage.submit")}
        </button>
        <p className="text-sm text-slate-500 text-center">
          {t("signupPage.haveAccount")}{" "}
          <Link to="/login" className="text-brand-600 hover:underline">
            {t("signupPage.login")}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
